import React, { useContext } from 'react';
import { BadgeCheck, Bookmark, BookmarkCheck, History, Loader2, ShieldAlert } from 'lucide-react';
import SaveBurst from '../vault/SaveBurst';
import { VaultThemeContext } from '../../pages/vault/useVaultTheme';
import '../../pages/vault/vault.css';

// What the trust control says (#283). It never shows without a public record.
export type TrustState = 'verified' | 'changed' | 'history';

const TRUST: Record<TrustState, { Icon: typeof BadgeCheck; label: string; aria: string }> = {
  verified: { Icon: BadgeCheck, label: 'Verified', aria: 'Verified: see what this means' },
  changed: { Icon: ShieldAlert, label: 'Photo changed', aria: 'A photo has changed since it was registered: see the details' },
  history: { Icon: History, label: 'Earlier version', aria: "You're looking at an earlier version: see the details" },
};

interface ProductActionsProps {
  // 'dock' floats at the bottom of phones; 'inline' sits under the details on wide screens.
  variant: 'dock' | 'inline';
  // null: no public record behind the label, so no claim and no control.
  trust: TrustState | null;
  onTrust: () => void;
  onSave?: () => void;
  isSaved: boolean;
  isSaving: boolean;
  // Disables Save without the spinner (e.g. while the viewer's auth resolves).
  saveDisabled?: boolean;
  // Bump to burst confetti from Save (a piece just landed in the vault).
  burst?: number;
  burstColours?: string[];
}

// The product page's two actions, in the vault's look: Save, and one compact
// "Verified" control whose evidence (the public record, the photos, earlier
// versions) lives in the sheet it opens.
const ProductActions: React.FC<ProductActionsProps> = ({
  variant,
  trust,
  onTrust,
  onSave,
  isSaved,
  isSaving,
  saveDisabled,
  burst = 0,
  burstColours,
}) => {
  const theme = useContext(VaultThemeContext);
  const t = trust ? TRUST[trust] : null;
  const trustButton = t && (
    <button type="button" className="v-trust" data-state={trust} onClick={onTrust} aria-label={t.aria}>
      <t.Icon size={18} strokeWidth={2.2} aria-hidden="true" />
      {t.label}
    </button>
  );
  const saveButton = onSave && (
    <button
      type="button"
      className="v-save"
      onClick={onSave}
      disabled={isSaving || saveDisabled}
      aria-pressed={isSaved}
    >
      <SaveBurst fire={burst} colours={burstColours} />
      {isSaving ? (
        <Loader2 size={18} className="animate-spin" aria-hidden="true" />
      ) : isSaved ? (
        <BookmarkCheck size={18} aria-hidden="true" />
      ) : (
        <Bookmark size={18} aria-hidden="true" />
      )}
      {isSaved ? 'In your vault' : 'Save to vault'}
    </button>
  );

  if (!trustButton && !saveButton) return null;
  if (variant === 'dock') {
    return (
      <div className="vault v-product-dock" data-vault-theme={theme}>
        {trustButton}
        {saveButton}
      </div>
    );
  }
  return (
    <div className="vault flex flex-wrap items-center gap-3" data-vault-theme={theme}>
      {trustButton}
      {saveButton}
    </div>
  );
};

export default ProductActions;
